import { Flex, Text } from "@radix-ui/themes";
import { InfoCircledIcon } from "@radix-ui/react-icons";
import { twMerge } from "tailwind-merge";
import { MessageCodes } from "./checkout-status";
import { useTranslation } from "react-i18next";
import { MessageResponse } from "../api/hitl-api";
import { VideoPlaceholder } from "./video-placeholde";
import { CancelButton } from "./cancel-button";

type InstructionsProps = {
  open?: boolean;
  messageContent: MessageResponse["result"];
  layout?: "page" | "modal";
  loading?: boolean;
  onCancel?: () => void;
};

export function Instructions({
  layout = "page",
  onCancel,
  loading,
  messageContent,
  open,
}: InstructionsProps) {
  const isModal = layout === "modal";
  const { t } = useTranslation();

  const uniqueErrorCodeLabels = Array.from(
    new Set(messageContent?.message_codes?.map((code) => t(`messages.${code}`)) || []).values()
  )
    .join(", ")
    .trimEnd();

  const errorCodes = messageContent?.message_codes;
  const hasError = !!messageContent && messageContent.message_codes.length > 0;
  const COUNTDOWN_TIME_ON_MODAL = 30;

  return (
    <div className={isModal ? "py-0 px-8 h-full" : "p-8 h-full"}>
      <div className="flex flex-col items-center gap-6 h-full relative">
        <Flex className="absolute top-4 right-6">
          <CancelButton
            activateCountdown={open}
            countdown={isModal ? COUNTDOWN_TIME_ON_MODAL : undefined}
            loading={loading}
            onClick={onCancel}
          />
        </Flex>
        <div className="w-full bg-white rounded-2xl border border-card-border p-6 pt-4">
          <Text size="8" weight="medium" mb="6" align="left" as="p" color="gray" highContrast>
            {t("instructionTitle")}
          </Text>

          <ul className="mt-4 flex gap-6">
            <li className={twMerge(["flex flex-col items-center gap-2"])}>
              <figure
                className={twMerge([
                  "rounded-2xl overflow-hidden min-w-[107px] w-full aspect-square",
                  errorCodes?.includes(MessageCodes.NoItems) &&
                    "ring ring-[#FFBA1A] bg-[rgba(255,179,1,0.18)]",
                ])}
              >
                <video
                  playsInline
                  preload="metadata"
                  className="min-w-[209.5px] aspect-square"
                  src="/instruction-01-cut-600px.webm"
                  poster="/instruction-01-poster.jpg"
                  autoPlay
                  muted
                  loop
                />
              </figure>
              <Text color={errorCodes?.includes(MessageCodes.NoItems) ? "brown" : "gray"} size="3">
                1. {t("instruction.1")}
              </Text>
            </li>
            <li className={twMerge(["flex flex-col items-center gap-2"])}>
              <figure
                className={twMerge([
                  "rounded-2xl overflow-hidden min-w-[107px] w-full aspect-square",
                  errorCodes?.includes(MessageCodes.DownwardItems) &&
                    "ring ring-[#FFBA1A] bg-[rgba(255,179,1,0.18)]",
                ])}
              >
                <video
                  playsInline
                  preload="metadata"
                  className="min-w-[209.5px] aspect-square"
                  src="/instruction-02-cut-600px.webm"
                  poster="/instruction-02-poster.jpg"
                  autoPlay
                  muted
                  loop
                />
              </figure>
              <Text size="3" color={errorCodes?.includes(MessageCodes.DownwardItems) ? "brown" : "gray"}>
                2. {t("instruction.2")}
              </Text>
            </li>
            <li className={twMerge(["flex flex-col items-center gap-2"])}>
              <figure
                className={twMerge([
                  "rounded-2xl overflow-hidden min-w-[107px] w-full aspect-square",
                  errorCodes?.includes(MessageCodes.StackedItems) &&
                    "ring ring-[#FFBA1A] bg-[rgba(255,179,1,0.18)]",
                ])}
              >
                <video
                  playsInline
                  preload="metadata"
                  className="min-w-[209.5px] aspect-square"
                  src="/instruction-03-cut-600px.webm"
                  poster="/instruction-03-poster.jpg"
                  autoPlay
                  muted
                  loop
                />
              </figure>
              <Text color={errorCodes?.includes(MessageCodes.StackedItems) ? "brown" : "gray"} size="3">
                3. {t("instruction.3")}
              </Text>
            </li>
            <li className={twMerge(["flex flex-col items-center gap-2"])}>
              <figure
                className={twMerge([
                  "min-w-[107px] w-full aspect-square rounded-2xl overflow-hidden",
                  errorCodes?.includes(MessageCodes.LeanOnItems) &&
                    "ring ring-[#FFBA1A] bg-[rgba(255,179,1,0.18)]",
                ])}
              >
                <video
                  playsInline
                  preload="metadata"
                  className="min-w-[209.5px] aspect-square"
                  src="/instruction-04-cut-600px.webm"
                  poster="/instruction-04-poster.jpg"
                  autoPlay
                  muted
                  loop
                />
              </figure>
              <Text color={errorCodes?.includes(MessageCodes.LeanOnItems) ? "brown" : "gray"} size="3">
                4. {t("instruction.4")}
              </Text>
            </li>
          </ul>
        </div>
        <div className="w-full flex flex-grow items-center gap-6 bg-white border border-card-border rounded-2xl p-6">
          <VideoPlaceholder className="h-[calc(100vh-550px)] xl:max-h-[180px]" />

          <div className="flex flex-grow items-center gap-4 mr-4">
            <div>
              {hasError ? (
                <figure className="w-12 h-12 overflow-hidden">
                  <img src="/warning.png" className="w-full h-full object-cover scale-[1.5]" />
                </figure>
              ) : (
                <InfoCircledIcon width={48} height={48} color="green" />
              )}
            </div>
            <div>
              {hasError ? (
                <>
                  <Text weight="medium" size="6" color="gray" highContrast as="p" mb="2">
                    {uniqueErrorCodeLabels}
                  </Text>
                  <Text as="p" size="3" color="gray">
                    {t("videoErrorDescription")}
                  </Text>
                </>
              ) : (
                <>
                  <Text weight="medium" size="6" color="gray" highContrast as="p" mb="2">
                    {t("videoTitle")}
                  </Text>
                  <Text className="whitespace-nowrap" as="p" size="3" color="gray">
                    {t("videoDescription")}
                  </Text>
                  <div className="indeterminate-progress-bar mt-5">
                    <div className="indeterminate-progress-bar__progress"></div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
